import { pickBy, isObject, isEmpty, assign, omitBy } from 'lodash';
import moment from 'moment';
import { EPrescriptionHVLPMedicament } from '../Components/EPrescriptionMedicamentsHVLPForm';
import { EPrescriptionIPLPMedicament } from '../Components/EPrescriptionMedicamentsIPLPForm';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import { EPrescriptionWizardData } from '../Components/EPrescriptionWrapper';

interface TransformedFormData {
	patient: {
		firstName: string;
		lastName: string;
		insuranceNumber: number;
		insuranceCompanyCode: string;
		phoneNumber?: string;
		email: string;
		dateOfBirth: string;
		gender: string | null;
		documentType: string | null;
		documentNumber: string;
		prisonName: string;
		address: {
			city: string;
			postCode: string;
			street: string;
			houseNumber: string;
			descriptiveNumber: number;
			registrationNumber: string;
			orientationNumber: string;
			county: string;
			district: string;
		};
	};
	recipe: {
		validTo: string;
		isAcute: boolean;
		isAdAsumProprium: boolean;
		typeInsurance: string | undefined;
		note: string;
		notification: string | undefined;
		repetitiveCount: number;
		recommendingDoctor?: {
			firstName: string;
			lastName: string;
			expertise: string;
			name: string;
			icz: string;
			icp: string;
			ic: string;
			dic: string;
			phoneNumber: number;
		};
	};
	recommendingDoctor?: {
		firstName: string;
		lastName: string;
		expertise: string;
		name: string;
		icz: string;
		icp: string;
		ic: string;
		dic: string;
		phoneNumber: number;
	};
	medicaments?: TransformedMedicament[];
	//   medicaments?: {
	//     hvlp: TransformedMedicament[];
	//     iplp: TransformedMedicament[];
	//   };
}

interface TransformedIngredients {
	quantity: number;
	unit: string;
	name: string;
	material?: string;
}

interface TransformedMedicament {
	type: 'hvlp' | 'iplp';
	hvlp?: { id: string };
	name?: string;
	iplp?: {
		name?: string;
		instruction?: string;
		form?: string;
		routeOfAdministration?: string;
	};
	quantity: number;
	manual: string;
	diagnosis: string | undefined;
	associatedDiagnosis: string | undefined;
	payment: string;
}

const cleanObject = (obj): Record<string, any> => {
	const finalObj: Record<string, any> = {};
	Object.keys(obj).forEach(key => {
		if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
			const nestedObj = cleanObject(obj[key]);
			if (Object.keys(nestedObj).length) {
				finalObj[key] = nestedObj;
			}
		} else if (Array.isArray(obj[key])) {
			if (obj[key].length) {
				obj[key].forEach(x => {
					const nestedObj = cleanObject(x);
					if (Object.keys(nestedObj).length) {
						finalObj[key] = finalObj[key]
							? [...finalObj[key], nestedObj]
							: [nestedObj];
					}
				});
			}
		} else if (
			obj[key] !== 'undefined' &&
			obj[key] !== undefined &&
			obj[key] !== null
		) {
			finalObj[key] = obj[key];
		}
	});
	return finalObj;
};

const transformMedicament = (
	medicament: EPrescriptionHVLPMedicament | EPrescriptionIPLPMedicament,
): TransformedMedicament | undefined => {
	let transformedMedicament: TransformedMedicament;

	// check if IPLP
	if (medicament.formType === PrescriptionType.IPLP) {
		transformedMedicament = {
			type: 'iplp',
			quantity: medicament.quantity,
			manual: medicament.manual,
			diagnosis:
				medicament.diagnosis?.value && medicament.diagnosis?.value?.length > 0
					? medicament.diagnosis?.value
					: undefined,
			associatedDiagnosis: medicament.associatedDiagnosis?.value,
			payment: medicament.payment?.value ?? '',
			iplp: {
				name: medicament.name,
				instruction: medicament?.instruction,
				form: medicament.form?.value ?? '',
				routeOfAdministration: medicament.routeOfAdministration?.value ?? '',
			},
		};
	} else {
		transformedMedicament = {
			name: medicament.item.name ?? '',
			hvlp: { id: medicament.item.id },
			quantity: medicament.quantity,
			payment: medicament.payment?.value ?? '',
			manual: medicament.manual,
			diagnosis: medicament.diagnosis?.value ?? undefined,
			associatedDiagnosis: medicament.associatedDiagnosis?.value ?? undefined,
			type: 'hvlp',
		};
	}

	return transformedMedicament;
};

const handleInsuranceCompanyCode = (insuranceCompanyCode?: string) => {
	if (insuranceCompanyCode && insuranceCompanyCode !== '999') {
		return insuranceCompanyCode;
	}
	return '';
};

const transformPrescriptionData = (
	formData: EPrescriptionWizardData,
): TransformedFormData | undefined => {
	if (
		!formData.patientInformation ||
		!formData.medicaments ||
		!formData.issuePrescription
	)
		return;

	let email = formData.patientInformation.electronicMail ?? '';
	if (formData.issuePrescription.notification?.value === 'email') {
		email = formData.issuePrescription?.notificationEmail || email;
	}

	let phoneNumber = formData.patientInformation.phoneNumber ?? undefined;
	if (formData.issuePrescription.notification?.value === 'sms') {
		phoneNumber =
			formData.issuePrescription?.notificationPhoneNumber || phoneNumber;
	}

	let transformedData: TransformedFormData = {
		patient: {
			firstName: formData.patientInformation.firstName + '',
			lastName: formData.patientInformation.lastName + '',
			insuranceNumber: formData.patientInformation.insuranceNumber ?? null,
			insuranceCompanyCode: handleInsuranceCompanyCode(
				formData?.patientInformation?.insuranceCompany?.value,
			),
			phoneNumber,
			email,
			dateOfBirth: moment(formData.patientInformation.dateOfBirth).format(
				'YYYY-MM-DD',
			),
			gender:
				formData.patientInformation.gender?.value === 'muz'
					? 'M'
					: formData.patientInformation.gender?.value === 'zena'
					? 'F'
					: null,
			documentType:
				typeof formData.patientInformation.idType?.value === 'string'
					? formData.patientInformation.idType?.value
					: null,
			documentNumber: formData.patientInformation.documentNumber + '',
			prisonName: formData.patientInformation.nameOfPrison ?? '',
			address: {
				city: formData.patientInformation.city + '',
				postCode: formData.patientInformation.postCode + '',
				street: formData.patientInformation.street + '',
				houseNumber: formData.patientInformation.guideNumber + '',
				descriptiveNumber: formData.patientInformation.descriptiveNumber ?? 0,
				registrationNumber: formData.patientInformation.evidenceNumber + '',
				orientationNumber: formData.patientInformation.guideNumber + '',
				county: formData.patientInformation.county ?? '',
				district: formData.patientInformation.district ?? '',
			},
		},
		recipe: {
			validTo: moment(formData.issuePrescription.validTo).format('YYYY-MM-DD'),
			isAcute: !!formData.issuePrescription.acute,
			isAdAsumProprium: !!formData.issuePrescription.adUsumProprium,
			typeInsurance:
				formData.patientInformation.insuranceType?.value ?? undefined,
			note: formData.issuePrescription.note ?? '',
			notification: formData.issuePrescription.notification?.value ?? undefined,
			...(formData.issuePrescription.repeatAbleCheck
				? { repetitiveCount: formData.issuePrescription.repeating ?? 1 }
				: {}),
			recommendingDoctor: {
				firstName: formData.issuePrescription.doctorFirstName ?? '',
				lastName: formData.issuePrescription.doctorLastName ?? '',
				expertise: formData.issuePrescription.expertise?.value ?? '',
				name: formData.issuePrescription.providerName ?? '',
				icz: formData.issuePrescription.icz + '',
				icp: formData.issuePrescription.icp + '',
				ic: formData.issuePrescription.ic + '',
				dic: formData.issuePrescription.dic + '',
				phoneNumber: formData.issuePrescription.doctorPhoneNumber ?? 0,
			},
		},
	};

	transformedData.recipe.notification =
		transformedData.recipe.notification === 'zadna'
			? undefined
			: transformedData.recipe.notification?.toUpperCase();

	if (!formData.issuePrescription.recommendingDoctorCheck) {
		delete transformedData.recipe.recommendingDoctor;
	}

	const medicaments: TransformedMedicament[] = [];
	if (formData.medicaments.hvlp) {
		for (const medicament of formData.medicaments.hvlp) {
			const transformedMedicament = transformMedicament(medicament);
			if (transformedMedicament) medicaments.push(transformedMedicament);
		}
	}

	if (formData.medicaments.iplp) {
		for (const medicament of formData.medicaments.iplp) {
			const transformedMedicament = transformMedicament(medicament);
			if (transformedMedicament) medicaments.push(transformedMedicament);
		}
	}

	transformedData = {
		...transformedData,
		medicaments,
	};

	return cleanObject(transformedData) as TransformedFormData;
};

export { transformPrescriptionData };
